/*
  CUSTOM: Towel Weekend free-gift logic.
  Mechanic: ONE free pair of Towel Club Slippers per qualifying item in the cart.
  A Shopify uncapped Buy-X-Get-Y discount zeroes up to N slippers when there are
  N qualifying items. This script's CARDINAL job is the inverse guard: the number
  of free-gift slipper units must NEVER exceed the number of qualifying items —
  any surplus would ring at full price and wrongly charge the customer.

  - PDP picker: after a qualifying jumpsuit is added, auto-adds ONE unit of the
    chosen slipper (with the _op_free_gift property). theme-event hook + fallback.
  - Cart-drawer picker: shows while the customer is entitled to MORE free slippers
    than they have claimed (qualifyingCount > giftCount); "Add my free slippers"
    claims another unit.
  - Over-count guard: whenever the cart changes, if giftCount > qualifyingCount it
    trims exactly (giftCount - qualifyingCount) gift units via /cart/change.js,
    most-recently-added first. This subsumes the old "orphan cleanup".
  - Tags the $0 slipper line(s) with a "Free gift" badge after each drawer refresh.

  SAFETY: only ever reads/trims slipper lines that carry the _op_free_gift
  property (surfaced by custom-towel-gift-state.liquid). Slippers bought normally
  (no property) are never counted and never touched. Idempotent; inflight-guarded.

  AUTHOR: Claude / Robb
  DATE: 2026-07-26
  DEPENDS ON (documented deviations from full isolation, each with a fallback):
  - Listens for Broadcast's `theme:product:add` event (assets/theme.dev.js
    dispatches it on the ATC button after a successful /cart/add). Fallback: a
    native capture-phase `submit` listener + /cart.js verification, so the
    auto-add still works if Precipio renames the event.
  - Dispatches `theme:cart:refresh`, which Broadcast's CartItems listens to
    (document.addEventListener('theme:cart:refresh', this.getCart)) to rebuild
    the drawer. Fallback: worst case the drawer shows stale content until the
    next open/update; cart contents stay correct.
  - Observes `[data-items-holder]` / `cart-drawer` (Broadcast data attributes)
    with a MutationObserver to re-sync after drawer refreshes.
  Everything else uses Shopify's public Cart AJAX API (/cart.js, /cart/add.js,
  /cart/change.js) and op- prefixed DOM only.
*/
(function () {
  'use strict';

  if (window.__opTowelGiftInit) return;
  window.__opTowelGiftInit = true;

  /* Hardcoded fallback variant IDs — used only if Liquid could not resolve the
     products via all_products (data-fallback="true" on the color input). */
  var FALLBACK_VARIANTS = {
    'towel-club-slippers-dusty-green': { sm: 54325489467764, ml: 54325489500532 },
    'towel-club-slippers-navy': { sm: 54325489533300, ml: 54325489566068 },
    'towel-club-slippers-off-white': { sm: 54325489598836, ml: 54325489631604 }
  };

  var GIFT_PROPERTY_KEY = '_op_free_gift';
  var GIFT_PROPERTY_VALUE = 'towel-weekend-2026';
  var BADGE_CLASS = 'op-gift-line-badge';

  var inflight = false;

  /* Per-product dedup: which qualifying products we just auto-added a gift for.
     Lets the submit fallback skip a product the primary theme-event already
     handled, while still allowing a DIFFERENT qualifying product to get its own
     gift. Never blocks legitimate per-product entitlement. */
  var handledAt = {};
  var HANDLED_TTL_MS = 5000;
  function markHandled(productId) {
    if (productId) handledAt[productId] = Date.now();
  }
  function handledRecently(productId) {
    var t = handledAt[productId];
    return !!t && Date.now() - t < HANDLED_TTL_MS;
  }

  /* ------------------------------------------------------------------ */
  /* Helpers                                                             */
  /* ------------------------------------------------------------------ */

  function withinWindow(picker) {
    var start = parseInt(picker.getAttribute('data-start'), 10);
    var end = parseInt(picker.getAttribute('data-end'), 10);
    if (isNaN(start) || isNaN(end)) return false;
    var now = Math.floor(Date.now() / 1000);
    return now >= start && now <= end;
  }

  function getPicker(mode) {
    return document.querySelector('[data-op-gift-picker][data-mode="' + mode + '"]');
  }

  function variantsForColorInput(input) {
    var handle = input.value;
    if (input.getAttribute('data-fallback') === 'true') {
      var fb = FALLBACK_VARIANTS[handle] || {};
      return {
        sm: { id: fb.sm || null, available: true },
        ml: { id: fb.ml || null, available: true }
      };
    }
    return {
      sm: {
        id: parseInt(input.getAttribute('data-sm-id'), 10) || null,
        available: input.getAttribute('data-sm-available') === 'true'
      },
      ml: {
        id: parseInt(input.getAttribute('data-ml-id'), 10) || null,
        available: input.getAttribute('data-ml-available') === 'true'
      }
    };
  }

  /* Returns the currently selected, available variant id, or null. */
  function getSelection(picker) {
    if (!picker) return null;
    var color = picker.querySelector('[data-op-gift-color]:checked');
    var size = picker.querySelector('[data-op-gift-size]:checked');
    if (!color || !size) return null;
    var variants = variantsForColorInput(color);
    var chosen = variants[size.value];
    if (chosen && chosen.id && chosen.available) return chosen.id;
    return null;
  }

  function isOptedOut(picker) {
    var optout = picker && picker.querySelector('[data-op-gift-optout]');
    return !!(optout && optout.checked);
  }

  /* Disable size pills that are unavailable for the checked color; if the
     checked size just became unavailable, hop to the other size. If a whole
     color is sold out, disable its swatch. */
  function syncAvailability(picker) {
    if (!picker) return;
    var color = picker.querySelector('[data-op-gift-color]:checked');
    if (!color) return;
    var variants = variantsForColorInput(color);

    picker.querySelectorAll('[data-op-gift-size]').forEach(function (sizeInput) {
      var v = variants[sizeInput.value];
      var ok = !!(v && v.id && v.available);
      sizeInput.disabled = !ok;
      sizeInput.closest('.op-gift__pill').classList.toggle('op-gift__pill--soldout', !ok);
      if (!ok && sizeInput.checked) {
        var other = picker.querySelector('[data-op-gift-size]:not([value="' + sizeInput.value + '"])');
        var otherV = other && variants[other.value];
        if (other && otherV && otherV.id && otherV.available) other.checked = true;
      }
    });

    picker.querySelectorAll('[data-op-gift-color]').forEach(function (colorInput) {
      var cv = variantsForColorInput(colorInput);
      var anyAvailable = (cv.sm.id && cv.sm.available) || (cv.ml.id && cv.ml.available);
      colorInput.disabled = !anyAvailable;
      colorInput.closest('.op-gift__swatch').classList.toggle('op-gift__swatch--soldout', !anyAvailable);
    });
  }

  /* On init: if the default (first color, S/M) is sold out, move the selection
     to the first fully available combination. */
  function ensureValidDefault(picker) {
    if (!picker || getSelection(picker)) return;
    var colors = picker.querySelectorAll('[data-op-gift-color]');
    var sizes = picker.querySelectorAll('[data-op-gift-size]');
    for (var i = 0; i < colors.length; i++) {
      var variants = variantsForColorInput(colors[i]);
      for (var j = 0; j < sizes.length; j++) {
        var v = variants[sizes[j].value];
        if (v && v.id && v.available) {
          colors[i].checked = true;
          sizes[j].checked = true;
          syncAvailability(picker);
          return;
        }
      }
    }
    /* Nothing available at all — hide the panel. */
    picker.hidden = true;
  }

  function refreshDrawer() {
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
  }

  /* Locale-aware route root (Shopify sets window.Shopify.routes.root). */
  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function fetchCart() {
    return fetch(routeRoot() + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  /* A cart line is one of OUR gifts iff it carries the _op_free_gift property.
     This is the ONLY thing we ever count or trim — never a normally-bought
     slipper. */
  function isGiftLine(item) {
    return !!(item && item.properties && item.properties[GIFT_PROPERTY_KEY]);
  }

  /* Add ONE gift unit (with the property). Guarded only by `inflight`; the
     over-count trim below is the authoritative ceiling, so per-product adds are
     free to stack up to the entitlement. */
  function ensureGift(variantId, onDone, productId) {
    if (!variantId || inflight) return;
    inflight = true;

    var properties = {};
    properties[GIFT_PROPERTY_KEY] = GIFT_PROPERTY_VALUE;
    fetch(routeRoot() + 'cart/add.js', {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: properties }] })
    })
      .then(function (response) {
        inflight = false;
        if (response && response.ok) {
          markHandled(productId);
          refreshDrawer();
        }
        if (typeof onDone === 'function') onDone(!!(response && response.ok));
      })
      .catch(function () {
        inflight = false;
        if (typeof onDone === 'function') onDone(false);
      });
  }

  /* Reduce a gift line to `quantity` (0 removes it). Only ever called with a
     line key sourced from the state marker's gift-lines (property-verified in
     Liquid), so we can never touch a non-gift line. */
  function changeGiftLine(lineKey, quantity) {
    if (!lineKey || inflight) return;
    inflight = true;
    fetch(routeRoot() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: quantity })
    })
      .then(function () {
        inflight = false;
        refreshDrawer();
      })
      .catch(function () {
        inflight = false;
      });
  }

  /* ------------------------------------------------------------------ */
  /* Drawer sync: picker visibility + "Free gift" badge + over-count trim */
  /* ------------------------------------------------------------------ */

  function getState() {
    /* Prefer the marker inside the drawer's refreshed items holder (always
       current); fall back to the static one rendered at page load. */
    var holder = document.querySelector('[data-items-holder]');
    var marker = holder && holder.querySelector('[data-op-gift-state]');
    if (!marker) marker = document.querySelector('[data-op-gift-state]');
    if (!marker) return null;
    var lines = [];
    try {
      lines = JSON.parse(marker.getAttribute('data-gift-lines') || '[]');
    } catch (e) {
      lines = [];
    }
    return {
      qualifyingCount: parseInt(marker.getAttribute('data-qualifying-count'), 10) || 0,
      giftCount: parseInt(marker.getAttribute('data-gift-count'), 10) || 0,
      giftLines: Array.isArray(lines) ? lines : []
    };
  }

  /* CARDINAL GUARD: free-gift slipper units must never exceed qualifying items.
     Trim exactly (giftCount - qualifyingCount) units, most-recently-added first
     (end of the cart-ordered gift-lines array). One /cart/change per pass; the
     refresh it triggers re-runs syncDrawer, which re-checks until surplus == 0. */
  function trimSurplusGifts(state) {
    if (inflight) return;
    var surplus = state.giftCount - state.qualifyingCount;
    if (surplus <= 0) return;

    var lines = state.giftLines;
    for (var i = lines.length - 1; i >= 0; i--) {
      var gl = lines[i];
      if (!gl || !gl.key) continue;
      var qty = parseInt(gl.qty, 10) || 0;
      if (qty <= 0) continue;
      var remove = Math.min(qty, surplus);
      changeGiftLine(gl.key, qty - remove);
      return; /* re-check happens on the refresh this triggers */
    }
  }

  function syncDrawer() {
    var cartPicker = getPicker('cart');
    var state = getState();
    if (!state) return;

    /* Show the cart picker while the customer is entitled to MORE free slippers
       than they have claimed. */
    if (cartPicker) {
      var show = withinWindow(cartPicker) && state.qualifyingCount > state.giftCount;
      cartPicker.hidden = !show;
      if (show) {
        syncAvailability(cartPicker);
        ensureValidDefault(cartPicker);
      }
    }

    /* Badge each $0 gift line. Broadcast already prints "Free" + the discount
       label; this adds the op- "Free gift" pill for extra clarity. */
    state.giftLines.forEach(function (gl) {
      if (!gl || !gl.free || !gl.key) return;
      var line = document.querySelector('[data-item="' + gl.key + '"]');
      if (line && !line.querySelector('.' + BADGE_CLASS)) {
        var badge = document.createElement('span');
        badge.className = BADGE_CLASS;
        badge.textContent = 'Free gift';
        line.appendChild(badge);
      }
    });

    /* Over-count guard (runs last so counts/badges are settled first). */
    trimSurplusGifts(state);
  }

  /* ------------------------------------------------------------------ */
  /* Wiring                                                              */
  /* ------------------------------------------------------------------ */

  function responseMatchesProduct(response, productId) {
    if (!response || !productId) return false;
    var pid = parseInt(productId, 10);
    if (response.product_id === pid) return true;
    if (Array.isArray(response.items)) {
      return response.items.some(function (item) {
        return item.product_id === pid;
      });
    }
    return false;
  }

  function handleProductAdded(response) {
    /* A page can hold several PDP-mode pickers (featured-product sections);
       act on the one whose product was just added. */
    var pickers = document.querySelectorAll('[data-op-gift-picker][data-mode="pdp"]');
    for (var i = 0; i < pickers.length; i++) {
      var picker = pickers[i];
      if (picker.hidden || !withinWindow(picker) || isOptedOut(picker)) continue;
      if (!responseMatchesProduct(response, picker.getAttribute('data-product-id'))) continue;
      var productId = parseInt(picker.getAttribute('data-product-id'), 10);
      markHandled(productId);
      ensureGift(getSelection(picker), null, productId);
      return;
    }
  }

  /* Primary ATC hook: Broadcast fires this on the button (bubbles to
     document) after a successful add — detail: { response, button }. */
  document.addEventListener('theme:product:add', function (event) {
    handleProductAdded(event.detail && event.detail.response);
  });

  /* Fallback ATC hook: native submit on the product form. Verifies via
     /cart.js after a short delay. Skips any product the primary theme-event
     already handled (handledRecently) so it never double-adds, but still fires
     per-product when the primary hook did not run. */
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || !form.getAttribute) return;
      var action = form.getAttribute('action') || '';
      if (action.indexOf('/cart/add') === -1) return;

      var candidates = [];
      document.querySelectorAll('[data-op-gift-picker][data-mode="pdp"]').forEach(function (picker) {
        if (picker.hidden || !withinWindow(picker) || isOptedOut(picker)) return;
        var productId = parseInt(picker.getAttribute('data-product-id'), 10);
        var variantId = getSelection(picker);
        if (productId && variantId) candidates.push({ productId: productId, variantId: variantId });
      });
      if (!candidates.length) return;

      setTimeout(function () {
        fetchCart().then(function (cart) {
          for (var i = 0; i < candidates.length; i++) {
            var c = candidates[i];
            if (handledRecently(c.productId)) continue; /* primary already added its gift */
            var match = (cart.items || []).some(function (item) {
              return item.product_id === c.productId && !isGiftLine(item);
            });
            if (match) {
              ensureGift(c.variantId, null, c.productId);
              return;
            }
          }
        });
      }, 1500);
    },
    true
  );

  /* Drawer picker button — claims one more unit of entitlement. Visible only
     while qualifyingCount > giftCount, so a click is always legitimate. */
  document.addEventListener('click', function (event) {
    var button = event.target.closest ? event.target.closest('[data-op-gift-add]') : null;
    if (!button) return;
    var picker = button.closest('[data-op-gift-picker]');
    if (!picker || !withinWindow(picker)) return;
    button.disabled = true;
    ensureGift(getSelection(picker), function () {
      button.disabled = false;
    });
  });

  /* Picker interactions. */
  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var picker = target.closest('[data-op-gift-picker]');
    if (!picker) return;

    if (target.hasAttribute('data-op-gift-color')) {
      syncAvailability(picker);
    }

    if (target.hasAttribute('data-op-gift-optout')) {
      picker.classList.toggle('op-gift--skipped', target.checked);
      var body = picker.querySelector('[data-op-gift-body]');
      if (body) body.hidden = target.checked;
    }
  });

  /* Re-sync the drawer UI whenever the cart changes. Three independent
     triggers, because <cart-drawer> is built by Broadcast AFTER page load and
     none of them can be relied on alone:
       1. document-level cart events (attached immediately — never skipped),
       2. a MutationObserver on <cart-drawer>, attached lazily once the
          element exists (the innerHTML swap happens below [data-items-holder]),
       3. a 2s heartbeat as vendor-proof fallback — cheap DOM reads; the
          trim and picker-visibility logic is idempotent. */
  function observeDrawer() {
    var scheduled = false;
    var queueSync = function () {
      if (scheduled) return;
      scheduled = true;
      /* setTimeout, NOT requestAnimationFrame: rAF never fires in hidden or
         non-painting tabs, which would stall the over-count trim entirely. */
      setTimeout(function () {
        scheduled = false;
        syncDrawer();
      }, 80);
    };
    ['theme:cart:refresh', 'theme:cart:change', 'theme:product:add'].forEach(function (evt) {
      document.addEventListener(evt, function () {
        setTimeout(queueSync, 600);
      });
    });
    var observed = null;
    var observer = new MutationObserver(queueSync);
    var attach = function () {
      var target = document.querySelector('cart-drawer');
      if (target && target !== observed) {
        if (observed) observer.disconnect();
        observer.observe(target, { childList: true, subtree: true });
        observed = target;
      }
    };
    attach();
    setInterval(function () {
      attach();
      queueSync();
    }, 2000);
  }

  function init() {
    /* Belt-and-braces time gate: Liquid already gates rendering, but a cached
       page can outlive the campaign window — hide any expired picker. */
    document.querySelectorAll('[data-op-gift-picker]').forEach(function (picker) {
      if (!withinWindow(picker)) {
        picker.hidden = true;
        return;
      }
      if (picker.getAttribute('data-mode') === 'pdp' || !picker.hidden) {
        syncAvailability(picker);
        ensureValidDefault(picker);
      }
    });
    observeDrawer();
    syncDrawer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
